import { PlusIcon } from "@heroicons/react/24/outline";
import { EPaymentMethod } from "common/enums/ProductSubscribed/EPaymentMethod";
import { ESubscriptionProductType } from "common/enums/ProductSubscribed/ESubscriptionProductType";
import { EProductType } from "common/enums/ProductSheet/EProductType";
import ProductSubscriptionCreateResource from "common/resources/ProductSubscribed/ProductSubscriptionCreateResource";
import ProductSubscriptionCorporationCreateResource from "common/resources/ProductSubscribed/ProductSubscriptionCorporationCreateResource";
import Button, { EButtonVariant } from "components/elements/Button";
import Modal from "components/elements/Modal";
import Table from "components/elements/Table";
import { ECellType, IRowProps, TableColumn } from "components/elements/Table/MuiTable";
import Typography, { ITypo } from "components/elements/Typography";
import InputElement, { EInputType } from "components/materials/Form/InputElement";
import MuiSelectInputElement, { IMuiSelectOption } from "components/materials/Form/MuiSelectInputElement";
import I18n from "components/materials/I18n";
import useOpenable from "hooks/useOpenable";
import { useCallback, useEffect, useMemo, useState } from "react";
import ProductService from "services/ProductService";
import { container } from "tsyringe";
import classes from "./classes.module.scss";
import DemembrementConfigTable from "components/pages/Subscriptions/elements/DemembrementConfigTable";
import DemembrementWarnings from "components/pages/Subscriptions/elements/DemembrementWarnings";
import { EDemembrementType } from "common/enums/ProductSubscribed/EDemembrementType";
import { getViagerRatio } from "utils/baremeViager";
import { ProductClasses, TProductName } from "common/resources/Scpi/PhysicalPerson/Products";
import ProductSheetResponseResource from "common/resources/ProductSheet/ProductSheetResponseResource";
import { useNavigate } from "react-router-dom";
import ClientSheetResponseResource from "common/resources/ClientSheet/ClientSheetResponseResource";
import CorporationSheetResponseResource from "common/resources/CorporationSheet/CorporationSheetResponseResource";
import ClientAdminApi from "api/wenImmo/admin/ClientApi";
import CorporationAdminApi from "api/wenImmo/admin/CorporationApi";
import { EAvailableProducts } from "common/enums/Scpi/EAvailableProduts";

const clientAdminApi = container.resolve(ClientAdminApi);
const corporationAdminApi = container.resolve(CorporationAdminApi);

export type IProductSelected = {
	product: ProductSheetResponseResource;
	dto: ProductSubscriptionCreateResource | ProductSubscriptionCorporationCreateResource;
};

type IProps = {
	productsInSubscription: IProductSelected[];
	setProductsInSubscription: (products: IProductSelected[]) => void;
	subscriberId: string;
	subscriberType: "physical" | "legal";
};

const productService = container.resolve(ProductService);

function calculateAge(birthDate: Date, currentDate: Date): number {
	let age = currentDate.getFullYear() - birthDate.getFullYear();

	// Vérifie si l'anniversaire est déjà passé cette année
	if (currentDate.getMonth() < birthDate.getMonth() || (currentDate.getMonth() === birthDate.getMonth() && currentDate.getDate() < birthDate.getDate())) {
		age--;
	}

	return age;
}

export default function ProductSelectorTable({ productsInSubscription, setProductsInSubscription, subscriberId, subscriberType }: IProps) {
	const [amountSelected, setAmountSelected] = useState<number>(1);
	const [productSelected, setProductSelected] = useState<ProductSheetResponseResource | null>(null);
	const [paymentMethodSelected, setPaymentMethodSelected] = useState<EPaymentMethod | null>(null);
	const [paymentMethodHasError, setPaymentMethodHasError] = useState<boolean>(false);
	const [subscriptionProductTypeSelected, setSubscriptionProductTypeSelected] = useState<ESubscriptionProductType>(ESubscriptionProductType.pleine_propriete);
	const [demembrementDuration, setDemembrementDuration] = useState<number | null>(null);
	const [demembrementType, setDemembrementType] = useState<EDemembrementType>(EDemembrementType.temporaire);
	const [viagerAge, setViagerAge] = useState<number | null>(null);
	const [contrepartieChoice, setContrepartieChoice] = useState<"avec" | "sans" | null>(null);
	const [contrepartieNom, setContrepartieNom] = useState<string | null>(null);
	const [products, setProducts] = useState<ProductSheetResponseResource[]>([]);
	const [userToSubscribe, setUserToSubscribe] = useState<ClientSheetResponseResource | CorporationSheetResponseResource | null>(null);
	const [productsAvailable, setProductsAvailable] = useState<ProductSheetResponseResource[]>([]);
	const [productType, setProductType] = useState<EProductType>(EProductType.scpi);
	const [pendingProductType, setPendingProductType] = useState<EProductType | null>(null);
	const [allVersionsOfSelectedProduct, setAllVersionsOfSelectedProduct] = useState<ProductSheetResponseResource[]>([]);
	const { isOpen, close, open } = useOpenable();

	const { isOpen: is85YearsModalOpened, close: close85YearsModal, open: open85YearsModal } = useOpenable();
	const { isOpen: isCategoryChangeModalOpen, close: closeCategoryChangeModal, open: openCategoryChangeModal } = useOpenable();
	const navigate = useNavigate();

	const lockedType = useMemo(() => productsInSubscription[0]?.dto.subscriptionProductType ?? null, [productsInSubscription]);

	useEffect(() => {
		if (lockedType) setSubscriptionProductTypeSelected(lockedType);
	}, [lockedType]);

	useEffect(() => {
		if (subscriberType === "legal") {
			corporationAdminApi.getCorporationSheetById(subscriberId).then(setUserToSubscribe);
		} else {
			clientAdminApi.getClientSheetByIdAdmin(subscriberId).then((client) => {
				setUserToSubscribe(client);
				setViagerAge(null);
			});
		}
	}, [subscriberId, subscriberType]);

	useEffect(() => {
		productService.getLastProductSheets().then(setProducts);
	}, []);

	useEffect(() => {
		setAmountSelected(productSelected?.minNumberOfShares ?? 1);
		setDemembrementDuration(null);
		setContrepartieChoice(null);
		setContrepartieNom(null);
	}, [productSelected?.minNumberOfShares]);

	useEffect(() => {
		const needsDemembrement = subscriptionProductTypeSelected !== ESubscriptionProductType.pleine_propriete;
		const productsFiltered = products
			.filter((productSheet) => productSheet.product?.productType === productType)
			.filter((productSheet) => !productsInSubscription.find((productInSubscription) => productInSubscription.product.id === productSheet.id))
			.filter((productSheet) => !needsDemembrement || (productSheet.demembrementKeys?.length ?? 0) > 0);
		setProductsAvailable(productsFiltered);
		setProductSelected(null);
	}, [products, productsInSubscription, productType, subscriptionProductTypeSelected]);

	const isDemembrementType = subscriptionProductTypeSelected !== ESubscriptionProductType.pleine_propriete;

	const demembrementRatio = useMemo(() => {
		if (!isDemembrementType) return 1;
		if (demembrementType === EDemembrementType.viager) {
			if (viagerAge === null) return 1;
			return getViagerRatio(viagerAge, subscriptionProductTypeSelected) ?? 1;
		}
		if (!demembrementDuration || !productSelected) return 1;
		const key = productSelected.demembrementKeys?.find((k) => k.duration === demembrementDuration);
		if (!key) return 1;
		return subscriptionProductTypeSelected === ESubscriptionProductType.usufruit ? key.usufruitRatio : key.nueProprieteRatio;
	}, [isDemembrementType, demembrementType, viagerAge, demembrementDuration, productSelected, subscriptionProductTypeSelected]);

	const redirectToHome = () => {
		close85YearsModal();
		navigate("/");
	};

	const handleProductTypeChange = useCallback(
		(newType: EProductType) => {
			// Si aucun produit sélectionné ou même type → changement libre
			if (productsInSubscription.length === 0 || productType === newType) {
				setProductType(newType);
				setProductSelected(null);
				setPaymentMethodSelected(null);
				return;
			}

			// Type différent et des produits déjà sélectionnés → demander confirmation
			setPendingProductType(newType);
			openCategoryChangeModal();
		},
		[productsInSubscription, productType, openCategoryChangeModal],
	);

	const confirmCategoryChange = useCallback(() => {
		if (pendingProductType) {
			setProductsInSubscription([]);
			setProductType(pendingProductType);
			setProductSelected(null);
			setPaymentMethodSelected(null);
			setPendingProductType(null);
		}
		closeCategoryChangeModal();
	}, [pendingProductType, setProductsInSubscription, closeCategoryChangeModal]);

	const cancelCategoryChange = useCallback(() => {
		setPendingProductType(null);
		closeCategoryChangeModal();
	}, [closeCategoryChangeModal]);

	const productSelectorColumsHead: TableColumn[] = useMemo(
		() => [
			{
				headContent: "Type de produit",
				key: "productType",
				widthPercentage: 15,
			},
			{
				headContent: I18n.asset.pages.subscriptions.select_products.table.columns.subscription_product_type,
				key: "subscriptionProductType",
				widthPercentage: 15,
			},
			{
				headContent: I18n.asset.pages.subscriptions.select_products.table.columns.scpi,
				key: "name",
				widthPercentage: 20,
			},
			{
				headContent: I18n.asset.pages.subscriptions.select_products.table.columns.paiement_method,
				key: "paymentMethod",
			},
			{
				headContent: I18n.asset.pages.subscriptions.select_products.table.columns.start_price,
				key: "price",
			},
			{
				headContent: I18n.asset.pages.subscriptions.select_products.table.columns.amount,
				key: "amount",
				widthPercentage: 10,
			},
			{
				headContent: I18n.asset.pages.subscriptions.select_products.table.columns.calculated_price,
				formatType: ECellType.EURO,
				key: "calculatedPrice",
			},
			{
				headContent: "",
				key: "addToCart",
				widthPercentage: 10,
			},
		],
		[],
	);

	const productTypeOptions: IMuiSelectOption[] = useMemo(() => {
		return Object.values(EProductType).map((type) => ({
			id: type,
			label: I18n.asset.enums.EProductType[type],
		}));
	}, []);

	const subscriptionProductTypeOptions: IMuiSelectOption[] = useMemo(
		() =>
			Object.values(ESubscriptionProductType).map((type) => ({
				id: type,
				label: I18n.asset.enums.ESubscriptionProductType[type],
			})),
		[],
	);

	// Obtenir tous les prix disponibles pour le produit sélectionné (sans doublons)
	const availablePrices = useMemo(() => {
		if (!productSelected || allVersionsOfSelectedProduct.length === 0) return [];
		const uniquePrices = Array.from(new Set(allVersionsOfSelectedProduct.map((sheet) => sheet.price)));
		return uniquePrices
			.sort((a, b) => a - b)
			.map((price) => ({
				id: price.toString(),
				label: `${price} €`,
			}));
	}, [productSelected, allVersionsOfSelectedProduct]);

	const paymentMethods = useMemo(() => {
		if (!productSelected) return [];
		const paymentMethods = ProductClasses[productSelected.product?.referenceName as TProductName].payment_methods.groups;
		return paymentMethods.filter((paymentMethod) => paymentMethod !== EPaymentMethod.CREDIT_CONSORTIUM).map((paymentMethod) => {
			const option: IMuiSelectOption = { id: paymentMethod, label: I18n.asset.enums.EPaymentMethod[paymentMethod] };
			return option;
		});
	}, [productSelected]);

	const setProductInSubscription = useCallback(() => {
		if (!productSelected || !paymentMethodSelected) return;

		const dto =
			subscriberType === "legal"
				? ProductSubscriptionCorporationCreateResource.hydrate<ProductSubscriptionCorporationCreateResource>({
						productSheetId: productSelected.id,
						quantity: +amountSelected,
						paymentMethod: paymentMethodSelected,
						corporationSubscriberId: subscriberId,
						subscriptionProductType: subscriptionProductTypeSelected,
						demembrementDuration: demembrementDuration ?? undefined,
						demembrementType,
						viagerAge: viagerAge ?? undefined,
					})
				: ProductSubscriptionCreateResource.hydrate<ProductSubscriptionCreateResource>({
						productSheetId: productSelected.id,
						quantity: +amountSelected,
						paymentMethod: paymentMethodSelected,
						subscriberId: subscriberId,
						subscriptionProductType: subscriptionProductTypeSelected,
						demembrementDuration: demembrementDuration ?? undefined,
						demembrementType,
						viagerAge: viagerAge ?? undefined,
						contrepartieNom: contrepartieChoice === "avec" ? contrepartieNom ?? undefined : undefined,
					});

		const productToAdd: IProductSelected = {
			product: productSelected,
			dto,
		};

		setProductsInSubscription([...productsInSubscription, productToAdd]);
		setProductSelected(null);
		setPaymentMethodSelected(null);
		setPaymentMethodHasError(false);
		setDemembrementDuration(null);
		setContrepartieChoice(null);
		setContrepartieNom(null);
		if (isOpen) close();
	}, [
		productSelected,
		paymentMethodSelected,
		amountSelected,
		subscriberId,
		subscriberType,
		subscriptionProductTypeSelected,
		demembrementDuration,
		demembrementType,
		viagerAge,
		contrepartieChoice,
		contrepartieNom,
		setProductsInSubscription,
		productsInSubscription,
		isOpen,
		close,
	]);

	const getMinNumberOfShares = useCallback(() => {
		if (!productSelected?.minNumberOfShares) return 1;
		return productSelected?.minNumberOfShares;
	}, [productSelected?.minNumberOfShares]);

	const addProductToSubscription = useCallback(() => {
		if (!productSelected) return console.warn("No product selected");
		if (!paymentMethodSelected) {
			console.warn("No payment method selected");
			return setPaymentMethodHasError(true);
		}
		if (isDemembrementType && demembrementType === EDemembrementType.temporaire && !demembrementDuration) return console.warn("No demembrement duration selected");
		if (isDemembrementType && demembrementType === EDemembrementType.viager && viagerAge === null) return console.warn("No viager age provided");
		if (isDemembrementType && contrepartieChoice === null) return console.warn("No contrepartie choice made");
		if (isDemembrementType && contrepartieChoice === "avec" && !contrepartieNom) return console.warn("No contrepartie nom provided");
		if (amountSelected < getMinNumberOfShares()) return open();

		// Get client age from his birthdate compared day to day (only for physical persons)
		if (subscriberType === "physical" && userToSubscribe && "birthDate" in userToSubscribe) {
			const clientSheet = userToSubscribe as ClientSheetResponseResource;
			const age = calculateAge(new Date(clientSheet.birthDate ?? ""), new Date());
			if (age >= 85 && productSelected.product?.referenceName === EAvailableProducts.kyaneos_pierre) return open85YearsModal();
		}
		setProductInSubscription();
	}, [
		productSelected,
		paymentMethodSelected,
		isDemembrementType,
		demembrementDuration,
		demembrementType,
		viagerAge,
		contrepartieChoice,
		contrepartieNom,
		amountSelected,
		getMinNumberOfShares,
		open,
		subscriberType,
		userToSubscribe,
		open85YearsModal,
		setProductInSubscription,
	]);

	const onProductSelectedChange = useCallback(
		async (values: Record<string, string | number> | null) => {
			if (!values) {
				setProductSelected(null);
				setAllVersionsOfSelectedProduct([]);
				return;
			}
			const selectedProduct = productsAvailable.find((product) => product.id === values["id"]);
			if (!selectedProduct) {
				setProductSelected(null);
				setAllVersionsOfSelectedProduct([]);
				return;
			}
			setProductSelected(selectedProduct);
			// Récupérer toutes les versions du produit sélectionné
			const allVersions = await productService.getAllProductSheetsByProductId(selectedProduct.productId);
			setAllVersionsOfSelectedProduct(allVersions);
		},
		[productsAvailable],
	);

	const onPriceSelectedChange = useCallback(
		(values: Record<string, string | number> | null) => {
			if (!values || !productSelected) return;
			const selectedPrice = parseFloat(values["id"] as string);
			const sheetWithPrice = allVersionsOfSelectedProduct.find((sheet) => sheet.price === selectedPrice);
			if (sheetWithPrice) {
				setProductSelected(sheetWithPrice);
			}
		},
		[productSelected, allVersionsOfSelectedProduct],
	);

	const onAmountChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		if (!e.target.value) return setAmountSelected(0.01);
		const value = parseFloat(e.target.value);
		setAmountSelected(value);
	}, []);

	const enrichedProducts: IRowProps[] = useMemo(() => {
		return [
			{
				rowKey: "todo",
				productType: (
					<MuiSelectInputElement
						name="productType"
						options={productTypeOptions}
						defaultValue={{ id: productType, label: I18n.asset.enums.EProductType[productType] }}
						onChange={(_event, option) => {
							if (option?.id) {
								handleProductTypeChange(option.id as EProductType);
							}
						}}
					/>
				),
				subscriptionProductType: (
					<MuiSelectInputElement
						name="subscriptionProductType"
						options={subscriptionProductTypeOptions}
						onChange={(_event, values) => {
							const type = values && (values["id"] as ESubscriptionProductType);
							if (type) setSubscriptionProductTypeSelected(type);
						}}
						defaultValue={{ id: subscriptionProductTypeSelected, label: I18n.asset.enums.ESubscriptionProductType[subscriptionProductTypeSelected] }}
						disabled={!!lockedType}
					/>
				),
				name: (
					<MuiSelectInputElement
						name="products"
						options={productsAvailable
							.sort((productA, productB) => productA.name.localeCompare(productB.name))
							.map((product) => {
								const option: IMuiSelectOption = { id: product.id, label: product.name };
								return option;
							})}
						onChange={(_event, values) => {
							onProductSelectedChange(values);
						}}
						defaultValue={productSelected ? { id: productSelected.id, label: productSelected.name } : null}
					/>
				),
				paymentMethod: (
					<MuiSelectInputElement
						name={"paymentMethods"}
						options={paymentMethods}
						onChange={(_event, values) => {
							const paymentMethod = values && (values["id"] as EPaymentMethod);
							setPaymentMethodSelected(paymentMethod);
						}}
						style={paymentMethodHasError ? { borderColor: "#EF4444", border: "1px solid #EF4444", boxSizing: "border-box" } : {}}
						defaultValue={paymentMethodSelected ? { id: paymentMethodSelected, label: I18n.asset.enums.EPaymentMethod[paymentMethodSelected] ?? "" } : null}
					/>
				),
				price: (
					<MuiSelectInputElement
						name="price"
						options={availablePrices}
						onChange={(_event, values) => {
							onPriceSelectedChange(values);
						}}
						defaultValue={productSelected ? { id: productSelected.price.toString(), label: `${productSelected.price} €` } : null}
					/>
				),
				amount: <InputElement name="price" type={EInputType.NUMBER} onChange={onAmountChange} defaultValue={amountSelected} min={0.01} step={0.01} />,
				calculatedPrice: +amountSelected * (productSelected?.price ?? 0) * demembrementRatio,
				addToCart: (
					<Button
						onClick={addProductToSubscription}
						disabled={
							isDemembrementType &&
							((demembrementType === EDemembrementType.temporaire ? !demembrementDuration : viagerAge === null) ||
								contrepartieChoice === null ||
								(contrepartieChoice === "avec" && !contrepartieNom))
						}>
						{I18n.asset.common.add}
						<PlusIcon />
					</Button>
				),
			},
		];
	}, [
		productTypeOptions,
		productType,
		subscriptionProductTypeOptions,
		subscriptionProductTypeSelected,
		lockedType,
		productsAvailable,
		productSelected,
		amountSelected,
		paymentMethodSelected,
		availablePrices,
		addProductToSubscription,
		onProductSelectedChange,
		onPriceSelectedChange,
		paymentMethods,
		onAmountChange,
		paymentMethodHasError,
		isDemembrementType,
		demembrementDuration,
		demembrementType,
		viagerAge,
		contrepartieChoice,
		contrepartieNom,
		demembrementRatio,
		handleProductTypeChange,
	]);

	return (
		<>
			<Table columnsHead={productSelectorColumsHead} rows={enrichedProducts} placeholderSearchbar="Chercher un produit" searchbarDisabled onNext={null} />
			{isDemembrementType && productSelected && (
				<DemembrementConfigTable
					product={productSelected}
					subscriptionProductType={subscriptionProductTypeSelected}
					demembrementType={demembrementType}
					onDemembrementTypeChange={setDemembrementType}
					demembrementDuration={demembrementDuration}
					onDurationChange={setDemembrementDuration}
					viagerAge={viagerAge}
					onViagerAgeChange={setViagerAge}
					contrepartieChoice={contrepartieChoice}
					onContrepartieChoiceChange={setContrepartieChoice}
					contrepartieNom={contrepartieNom}
					onContrepartieNomChange={setContrepartieNom}
				/>
			)}
			<DemembrementWarnings
				isDemembrementType={isDemembrementType}
				productSelected={!!productSelected}
				contrepartieChoice={contrepartieChoice}
				subscriptionProductTypeSelected={subscriptionProductTypeSelected}
			/>
			<Modal isOpen={isOpen} onClose={close} className={classes["modal"]}>
				<Typography typo={ITypo.H1}>{I18n.asset.common.warning}</Typography>
				<Typography typo={ITypo.P_MEDIUM_BOLD}>
					{I18n.trslt(I18n.asset.pages.subscriptions.select_products.modal.text, { minimumShare: getMinNumberOfShares().toString() })}
				</Typography>

				<div className={classes["button-container"]}>
					<Button onClick={close} variant={EButtonVariant.OUTLINED}>
						{I18n.asset.common.cancel}
					</Button>
					<Button onClick={setProductInSubscription}>{I18n.asset.pages.subscriptions.select_products.modal.button}</Button>
				</div>
			</Modal>
			<Modal isOpen={is85YearsModalOpened} onClose={close85YearsModal} className={classes["modal"]}>
				<Typography typo={ITypo.H1}>{I18n.asset.common.warning}</Typography>
				<Typography typo={ITypo.P_MEDIUM_BOLD}>{I18n.trslt(I18n.asset.pages.subscriptions.select_products.modal85years.text)}</Typography>

				<div className={classes["button-container"]}>
					<Button onClick={redirectToHome} variant={EButtonVariant.OUTLINED}>
						{I18n.asset.common.back_to_home}
					</Button>
					<Button onClick={close85YearsModal}>{I18n.asset.pages.subscriptions.select_products.modal85years.button}</Button>
				</div>
			</Modal>
			<Modal isOpen={isCategoryChangeModalOpen} onClose={cancelCategoryChange} className={classes["modal"]}>
				<Typography typo={ITypo.H1}>{I18n.asset.common.warning}</Typography>
				<Typography typo={ITypo.P_MEDIUM_BOLD}>
					{pendingProductType &&
						I18n.trslt(I18n.asset.pages.subscriptions.select_products.modalCategoryChange.text, {
							currentType: productType ? I18n.asset.enums.EProductType[productType] : "",
							newType: I18n.asset.enums.EProductType[pendingProductType],
						})}
				</Typography>

				<div className={classes["button-container"]}>
					<Button onClick={cancelCategoryChange} variant={EButtonVariant.OUTLINED}>
						{I18n.asset.common.cancel}
					</Button>
					<Button onClick={confirmCategoryChange}>{I18n.asset.pages.subscriptions.select_products.modalCategoryChange.button}</Button>
				</div>
			</Modal>
		</>
	);
}
